import { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";

import { useEditor, EditorContent } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";

import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";

import { TableKit } from "@tiptap/extension-table";

import { Extension } from "@tiptap/core";
import { Plugin } from "prosemirror-state";
import Dropcursor from "@tiptap/extension-dropcursor";

import LinkModal from "./linkmodal/LinkModal";
import ImageModal from "./customimage/ImageModal";
import prettier from "prettier/standalone";
import parserHtml from "prettier/plugins/html";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Link2,
  ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Quote,
  Code2,
  Trash2,
  ChevronDown,
  Undo2,
  Redo2,
  Rows3,
  Columns3,
  Merge,
  Split,
  Heading,
  MinusSquare,
  PlusSquare,
  Table2,
} from "lucide-react";
import TableContextMenu from "./TableContextMenu";

/* ===================== STYLE ===================== */

const EditorWrapper = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;

    padding: 8px;

    background: #fff;

    border-bottom: 1px solid #e5e7eb;
  }

  .editor {
    border-top: none;
    padding: 12px;
  }

  .toolbar-group {
    display: flex;
    align-items: center;
    gap: 2px;

    padding-right: 6px;
    margin-right: 6px;

    border-right: 1px solid #e5e7eb;
  }

  .toolbar-group:last-child {
    border-right: none;
  }

  .toolbar button {
    width: 34px;
    height: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: none;
    background: transparent;

    border-radius: 6px;
    cursor: pointer;
  }

  .toolbar button:hover {
    background: #f3f4f6;
  }

  .toolbar button.active {
    background: #e5e7eb;
  }

  .toolbar select {
    height: 34px;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 0 8px;
  }

  // .editor {
  //   padding: 12px;
  //   min-height: 300px;
  // }

  .table-menu {
    position: absolute;
    top: 40px;
    left: 0;

    min-width: 220px;

    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;

    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);

    overflow: hidden;

    z-index: 1000;
  }

  .table-menu button {
    width: 100%;
    height: 38px;

    display: flex;
    align-items: center;
    justify-content: flex-start;

    gap: 10px;

    padding: 0 12px;

    border: none;
    border-radius: 0;

    background: white;
  }

  .table-menu button:hover {
    background: #f3f4f6;
  }

  .table-divider {
    height: 1px;
    background: #e5e7eb;
  }

  .ProseMirror {
    outline: none;
  }

  .ProseMirror table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
  }

  .ProseMirror td,
  .ProseMirror th {
    border: 1px solid #ddd;
    min-width: 100px;
    padding: 8px;
    position: relative;
  }

  .ProseMirror th {
    background: #f5f5f5;
  }

  .resize-cursor {
    cursor: col-resize;
  }

  .table-bubble {
    display: flex;
    align-items: center;
    gap: 6px;

    padding: 8px;

    background: #ffffff;

    border: 1px solid #e5e7eb;
    border-radius: 14px;

    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.08),
      0 4px 10px rgba(0, 0, 0, 0.04);

    backdrop-filter: blur(12px);

    animation: bubbleIn 0.15s ease;
  }

  .table-bubble button {
    width: 34px;
    height: 34px;

    border: none;
    border-radius: 10px;

    background: transparent;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;

    transition: all 0.15s ease;
  }

  .table-bubble button:hover {
    background: #f3f4f6;
    transform: translateY(-1px);
  }

  .table-bubble button.danger:hover {
    background: #fee2e2;
    color: #dc2626;
  }

  .table-bubble-divider {
    width: 1px;
    height: 24px;
    background: #e5e7eb;
  }

  @keyframes bubbleIn {
    from {
      opacity: 0;
      transform: translateY(6px) scale(0.96);
    }

    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .ProseMirror .selectedCell {
    position: relative;
  }

  .ProseMirror .selectedCell::after {
    content: "";

    position: absolute;
    inset: 0;

    background: rgba(59, 130, 246, 0.18);

    pointer-events: none;
  }

  .ProseMirror table {
    overflow: hidden;
  }

  .ProseMirror td,
  .ProseMirror th {
    position: relative;
  }

  .ProseMirror table.no-border td,
  .ProseMirror table.no-border th {
    border: none !important;
  }

  .editor-split {
    display: flex;
    height: 100%;
  }

  .editor-pane,
  .html-pane {
    flex: 1;
  }

  .html-pane {
    border-left: 1px solid #ddd;
    padding: 12px;
    font-family: monospace;
    resize: none;
    outline: none;
  }

  .html-editor {
    width: 100%;
    height: 100%;
    min-height: 500px;
    border: none;
    outline: none;
    padding: 12px;
    font-family: Consolas, monospace;
    font-size: 14px;
    background: #0f172a;
    color: #e2e8f0;
    white-space: pre;
  }
`;

/* ===================== PASTE IMAGE (IMPORTANT FIX) ===================== */

const PasteImage = Extension.create({
  name: "pasteImage",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste(view, event) {
            const items = Array.from(event.clipboardData?.items || []);

            const item = items.find((i) => i.type.startsWith("image"));

            if (!item) return false;

            const file = item.getAsFile();
            if (!file) return false;

            const reader = new FileReader();

            reader.onload = () => {
              const src = reader.result as string;

              const node = view.state.schema.nodes.image.create({
                src,
              });

              const tr = view.state.tr.replaceSelectionWith(node);
              view.dispatch(tr);
            };

            reader.readAsDataURL(file);
            return true;
          },
        },
      }),
    ];
  },
});

/* ===================== FONT SIZE (REAL FIX) ===================== */

const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,

            parseHTML: (element) => element.style.fontSize,

            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },

      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize: null }).run();
        },
    };
  },
});

/* ===================== COMPONENT ===================== */

interface Props {
  value: string;
  onChange: (html: string) => void;
  height: number;
}

const TiptapEditor: React.FC<Props> = ({ value, onChange, height }) => {
  const [openLink, setOpenLink] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value || "");

  const [menu, setMenu] = useState({
    open: false,
    x: 0,
    y: 0,
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),

      TextStyle,
      Color,
      Highlight,
      Underline,
      FontSize,

      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),

      Link.configure({
        openOnClick: false,
      }),

      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Dropcursor,

      TableKit.configure({
        table: {
          resizable: true,
          handleWidth: 5,
          cellMinWidth: 50,
        },
      }),

      PasteImage,
    ],

    content: value || "",

    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      setHtmlContent(html);

      onChange(editor.getHTML());
    },
  });

  /* ===================== SYNC VALUE (FIX LOOP) ===================== */

  useEffect(() => {
    if (!editor) return;

    const current = editor.getHTML();
    if (value === current) return;

    editor.commands.setContent(value || "");
    setHtmlContent(value || "");
  }, [value]);

  useEffect(() => {
    const close = () =>
      setMenu({
        open: false,
        x: 0,
        y: 0,
      });

    window.addEventListener("click", close);

    return () => {
      window.removeEventListener("click", close);
    };
  }, []);

  /* ===================== LINK ===================== */

  const insertLink = (data: any) => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .setLink({
        href: data.url,
        target: "_blank",
        rel: data.nofollow ? "nofollow" : "",
      })
      .run();

    setOpenLink(false);
  };

  /* ===================== IMAGE ===================== */
  const cleanHtml = (html: string) => {
    return html.replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ");
  };
  const formatHtml = async (html: string): Promise<string> => {
    try {
      const result = await prettier.format(html, {
        parser: "html",
        plugins: [parserHtml],
      });

      return result;
    } catch {
      return html;
    }
  };

  const handleHtmlChange = (val: string) => {
    setHtmlContent(val);

    // chỉ update khi cần, KHÔNG realtime
    //editor.commands.setContent(val);
  };

  const insertImage = (data: any) => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .setImage({
        src: data.src,
        alt: data.alt || "",
      })
      .run();

    setOpenImage(false);
  };

  /* ===================== TOOLBAR ===================== */

  if (!editor) return null;

  return (
    <>
      <EditorWrapper>
        {/* TOOLBAR */}
        <div className="toolbar">
          {/* Undo Redo */}
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().undo().run()}>
              <Undo2 size={16} />
            </button>

            <button
              type="button"
              onClick={() => editor.chain().focus().redo().run()}>
              <Redo2 size={16} />
            </button>
          </div>
          <div className="toolbar-group">
            <button
              type="button"
              onClick={async () => {
                if (!htmlMode) {
                  const cleaned = cleanHtml(editor.getHTML());
                  const formatted = await formatHtml(cleaned);
                  setHtmlContent(formatted);
                } else {
                  // 👉 apply HTML vào editor
                  editor.commands.setContent(htmlContent);

                  // 👉 QUAN TRỌNG: cập nhật ra ngoài
                  onChange(htmlContent);
                }

                setHtmlMode(!htmlMode);
              }}>
              HTML
            </button>
          </div>

          {/* Heading */}
          <div className="toolbar-group">
            <select
              onChange={(e) => {
                const value = e.target.value;

                if (value === "0") {
                  editor.chain().focus().setParagraph().run();
                  return;
                }

                editor
                  .chain()
                  .focus()
                  .toggleHeading({
                    level: Number(value) as 1 | 2 | 3,
                  })
                  .run();
              }}>
              <option value="0">Text</option>
              <option value="1">H1</option>
              <option value="2">H2</option>
              <option value="3">H3</option>
            </select>
          </div>

          {/* Basic */}
          <div className="toolbar-group">
            <button
              type="button"
              className={editor.isActive("bold") ? "active" : ""}
              onClick={() => editor.chain().focus().toggleBold().run()}>
              <Bold size={16} />
            </button>

            <button
              type="button"
              className={editor.isActive("italic") ? "active" : ""}
              onClick={() => editor.chain().focus().toggleItalic().run()}>
              <Italic size={16} />
            </button>

            <button
              type="button"
              className={editor.isActive("underline") ? "active" : ""}
              onClick={() => editor.chain().focus().toggleUnderline().run()}>
              <UnderlineIcon size={16} />
            </button>

            <button
              type="button"
              className={editor.isActive("strike") ? "active" : ""}
              onClick={() => editor.chain().focus().toggleStrike().run()}>
              <Strikethrough size={16} />
            </button>
          </div>

          {/* Color */}
          <div className="toolbar-group">
            <input
              type="color"
              onChange={(e) =>
                editor.chain().focus().setColor(e.target.value).run()
              }
            />
          </div>

          {/* Align */}
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("left").run()}>
              <AlignLeft size={16} />
            </button>

            <button
              type="button"
              onClick={() =>
                editor.chain().focus().setTextAlign("center").run()
              }>
              <AlignCenter size={16} />
            </button>

            <button
              type="button"
              onClick={() =>
                editor.chain().focus().setTextAlign("right").run()
              }>
              <AlignRight size={16} />
            </button>
          </div>

          {/* Lists */}
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <List size={16} />
            </button>

            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <ListOrdered size={16} />
            </button>
          </div>

          {/* Quote Code */}
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}>
              <Quote size={16} />
            </button>

            <button
              type="button"
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
              <Code2 size={16} />
            </button>
          </div>

          {/* Link Image */}
          <div className="toolbar-group">
            <button type="button" onClick={() => setOpenLink(true)}>
              <Link2 size={16} />
            </button>

            <button type="button" onClick={() => setOpenImage(true)}>
              <ImageIcon size={16} />
            </button>
          </div>

          <div className="toolbar-group">
            <button
              type="button"
              title="Insert Table"
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({
                    rows: 3,
                    cols: 3,
                    withHeaderRow: true,
                  })
                  .run()
              }>
              <Table2 size={18} />
            </button>
          </div>

          {/* Clear */}
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().clearContent().run()}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {/* EDITOR */}
        <div
          style={{ height, overflowY: "scroll" }}
          className="editor"
          onContextMenu={(e) => {
            const target = e.target as HTMLElement;

            if (!target.closest("td,th")) return;

            e.preventDefault();

            setMenu({
              open: true,
              x: e.clientX,
              y: e.clientY,
            });
          }}>
          {htmlMode ? (
            <textarea
              className="html-editor"
              value={htmlContent}
              onChange={(e) => handleHtmlChange(e.target.value)}
            />
          ) : (
            <EditorContent editor={editor} />
          )}
        </div>

        {menu.open && (
          <TableContextMenu
            editor={editor}
            x={menu.x}
            y={menu.y}
            onClose={() =>
              setMenu({
                open: false,
                x: 0,
                y: 0,
              })
            }
          />
        )}
      </EditorWrapper>

      {/* MODALS */}
      <LinkModal
        open={openLink}
        onClose={() => setOpenLink(false)}
        onSubmit={insertLink}
      />

      <ImageModal
        open={openImage}
        onClose={() => setOpenImage(false)}
        onSubmit={insertImage}
      />
    </>
  );
};

export default TiptapEditor;
